import { domainProvider } from '@mathflat/shared/@common/domain/domainProvider'
import axios, { type AxiosError } from 'axios'
import { isObjectLike } from 'lodash-es'
import qs from 'qs'

import { TOAST_STR } from '@/constant'
import { CommonError } from '@/dto'
import WebviewService from '@/service/webview.service.ts'
import type { ResErr } from '@/types/common/api'
import { deleteNullField } from '@/utils/common'
import { envUtils } from '@/utils/envUtils.ts'
import { toastError } from '@/utils/toast'

import { datadogClient } from '../modules/datadog/datadogClient'
import { wrapErrorHandlerWithJwt, wrapRequestInterceptorWithJwt } from './jwtInterceptors'

const BASE_URL = (() => {
  if (envUtils.isLive) {
    return `https://search-api.${domainProvider.TARGET_DOMAIN}`
  }
  if (envUtils.isStaging) {
    return `https://search-api-staging.${domainProvider.TARGET_DOMAIN}`
  }

  return `https://search-api-dev.${domainProvider.TARGET_DOMAIN}`
})()

const XPlatform = (() => {
  if (WebviewService.isAndroid) return 'TEACHER_ANDROID'
  if (WebviewService.isIpad) return 'TEACHER_IOS'
  return 'TEACHER_WEB'
})()

export const searchAxios = axios.create({
  baseURL: BASE_URL,
  headers: {
    'x-platform': XPlatform,
    'x-freewheelin-host': envUtils.isSchool ? 'schoolflat.com' : domainProvider.TARGET_DOMAIN,
  },
  paramsSerializer: (params) => {
    // params가 null인 값은 서버로 보내지 않음
    const newParams = isObjectLike(params) ? deleteNullField(params) : params
    return qs.stringify(newParams, { arrayFormat: 'comma' })
  },
})

searchAxios.interceptors.request.use(wrapRequestInterceptorWithJwt((config) => config))

const searchJwtErrorHandler = wrapErrorHandlerWithJwt(
  async (error: unknown) => {
    const axiosError = error as AxiosError<ResErr>
    datadogClient.sendError(axiosError)

    if (axiosError.response) {
      const {
        config,
        data: { message, code, data },
        status,
      } = axiosError.response
      const err = new CommonError(message, { type: 'API', status, code, data, config })

      if (status === 500) {
        toastError(TOAST_STR.errorMessage)
      }

      return Promise.reject<CommonError>(err)
    }

    return Promise.reject(axiosError)
  },
  async (originalRequest, newAccessToken) => {
    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
    return searchAxios(originalRequest)
  },
)

searchAxios.interceptors.response.use((response) => response, searchJwtErrorHandler)
